import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import { useTranslation } from 'react-i18next';
import { getDeletedVisitsLogAPI } from '../../../api/protocol';
import { ViewDeletedVisit } from '../../Icons/Icons';
import { HandleApiError } from '../../Notifications/APIErrorHandler';
import { Tooltip } from '../../Utils/Tooltips';
import { dateTimeUTCFormatter } from '../../Utils/Formatter';

const REASON_LABELS = {
    0: 'visitTracking_removeVisit_reason_loadError',
    1: 'visitTracking_removeVisit_reason_other'
};

export const DeletedVisitsModal = ({ protocolId, onClose, onViewVisit }) => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [totalSize, setTotalSize] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, sizePerPage: 10 });

    useEffect(() => {
        fetchLogs(pagination.page, pagination.sizePerPage);
    }, [protocolId, pagination]);

    const fetchLogs = async (page, sizePerPage) => {
        setIsLoading(true);
        try {
            const res = await getDeletedVisitsLogAPI(protocolId, sizePerPage, page - 1);
            setLogs(res.data.data);
            setTotalSize(res.data.count);
        } catch (error) {
            HandleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTableChange = (type, { page, sizePerPage }) => {
        if (type === 'pagination')
            setPagination({ page, sizePerPage });
    };

    const columns = [
        {
            dataField: 'visitTitle',
            text: t('protocolNotifications.visitTracking_deletedVisits_col_visit'),
            sort: true
        },
        {
            dataField: 'patientId',
            text: t('protocolNotifications.visitTracking_deletedVisits_col_patient'),
            sort: true
        },
        {
            dataField: 'deletedByUserName',
            text: t('protocolNotifications.visitTracking_deletedVisits_col_deletedBy'),
            sort: true
        },
        {
            dataField: 'deletedDate',
            text: t('protocolNotifications.visitTracking_deletedVisits_col_date'),
            sort: true,
            formatter: (cell) => dateTimeUTCFormatter(cell)
        },
        {
            dataField: 'reasonType',
            text: t('protocolNotifications.visitTracking_deletedVisits_col_reason'),
            formatter: (cell, row) => {
                const label = t(`protocolNotifications.${REASON_LABELS[cell]}`);
                return row.reasonText ? `${label} — ${row.reasonText}` : label;
            }
        },
        {
            dataField: 'id',
            text: '',
            headerStyle: { width: '60px' },
            formatter: (cell, _row, rowIndex) => (
                <Tooltip tooltip={t('protocolNotifications.visitTracking_deletedVisit_banner_title')} id={`view-deleted-visit-${rowIndex}`}>
                    <button className="btn-table-actions" type="button" onClick={() => onViewVisit(cell)}>
                        <ViewDeletedVisit />
                    </button>
                </Tooltip>
            )
        }
    ];

    return (
        <Modal show onHide={onClose} bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title>{t('protocolNotifications.visitTracking_deletedVisits_title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <BootstrapTable
                    classes="table-spaced table-spaced-small"
                    wrapperClasses="table-responsive"
                    bordered={false}
                    hover
                    striped
                    condensed
                    remote
                    keyField='id'
                    data={logs}
                    columns={columns}
                    pagination={paginationFactory({
                        page: pagination.page,
                        sizePerPage: pagination.sizePerPage,
                        totalSize: totalSize,
                        hideSizePerPage: true
                    })}
                    onTableChange={handleTableChange}
                    noDataIndication={() => <>{t('commons.noData')}</>}
                    loading={isLoading}
                    overlay={overlayFactory({ spinner: true })}
                />
            </Modal.Body>
        </Modal>
    );
};
